import { AD_STORAGE_KEY } from '@/constants/ga';

const AD_PARAMS_KEY = AD_STORAGE_KEY.AD_PARAMS;

/**
 * 広告パラメータの有効期限（30日）
 */
const AD_PARAMS_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * UTM パラメータ一覧
 */
const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

/**
 * 各 UTM 値の最大文字数
 */
const MAX_UTM_VALUE_LENGTH = 200;

/**
 * Auth0 の ext-params に許可される最大文字数
 */
const AUTH0_EXT_PARAMS_MAX_LENGTH = 255;

/**
 * 広告パラメータ型定義
 */
export type AdParams = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  landing_url?: string;
  timestamp?: number;
};

/**
 * 外部入力の広告パラメータをサニタイズする。
 * ホワイトリスト方式で UTM_KEYS に含まれるキーのみを抽出し、
 * 文字列型かつ長さ制限内の値だけを返す。
 *
 * @param raw - JSON.parse 結果など、未検証の外部入力オブジェクト
 * @returns UTM キーのみを含む安全な AdParams
 */
export const sanitizeAdParams = (raw: Record<string, unknown>): AdParams => {
  const result: AdParams = {};
  UTM_KEYS.forEach((key) => {
    const val = raw[key];
    if (typeof val === 'string' && val.length <= MAX_UTM_VALUE_LENGTH) {
      result[key] = val;
    }
  });

  return result;
};

/**
 * URLパラメータから広告関連のパラメータを検出し保存する。
 * ラストタッチ方式: 新しいUTMパラメータが検出された場合、既存のパラメータを上書きする。
 */
export const captureAdParams = (): void => {
  const params = new URLSearchParams(window.location.search);

  const adParams: AdParams = {};
  let hasAdParam = false;

  // UTM パラメータ
  UTM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value && value.length <= MAX_UTM_VALUE_LENGTH) {
      adParams[key] = value;
      hasAdParam = true;
    }
  });

  if (hasAdParam) {
    // クエリ文字列にはsignup_code等の機微情報が含まれうるため、origin + pathname のみ保存する
    adParams.landing_url = window.location.origin + window.location.pathname;
    adParams.timestamp = Date.now();
    localStorage.setItem(AD_PARAMS_KEY, JSON.stringify(adParams));
  }
};

/**
 * 保存済みの広告パラメータを取得する
 */
export const getAdParams = (): AdParams | null => {
  const stored = localStorage.getItem(AD_PARAMS_KEY);
  if (!stored) return null;

  try {
    const parsed = JSON.parse(stored) as AdParams;
    // timestampがない、または有効期限を超過したパラメータは無効とする
    if (
      !parsed.timestamp ||
      Date.now() - parsed.timestamp > AD_PARAMS_EXPIRY_MS
    ) {
      localStorage.removeItem(AD_PARAMS_KEY);

      return null;
    }

    return parsed;
  } catch {
    localStorage.removeItem(AD_PARAMS_KEY);

    return null;
  }
};

/**
 * 広告パラメータをクリアする（コンバージョン送信完了後に呼ぶ）
 */
export const clearAdParams = (): void => {
  localStorage.removeItem(AD_PARAMS_KEY);
};

/**
 * Auth0に渡すためのシリアライズ済みパラメータを取得する。
 * Auth0のカスタムクエリパラメータとして `ext-ad_params` に付与する。
 *
 * @returns 末尾パディング（=）を除去した Base64 文字列、またはパラメータなしの場合 undefined。
 *          デコード側では `atob` 前に `=` を補完するか、パディングなしに対応したデコーダを使用すること。
 */
export const getAdParamsForAuth0 = (): string | undefined => {
  const params = getAdParams();
  if (!params) return undefined;

  try {
    // localStorage は改変可能なため、sanitizeAdParams でホワイトリスト（UTMキー）のみに絞る。
    // landing_url / timestamp 等の不要フィールドを除外し、URL長超過や情報露出リスクを低減する。
    const sanitized = sanitizeAdParams(params as Record<string, unknown>);

    // Auth0 の ext-params は /^[-\w.*~@+ /:]{1,255}$/ で検証され、標準 Base64 の `=`（パディング）が許可されないため除去する。
    const jsonStr = JSON.stringify(sanitized);
    const utf8Bytes = new TextEncoder().encode(jsonStr);
    const binaryStr = Array.from(utf8Bytes, (byte) =>
      String.fromCharCode(byte),
    ).join('');

    const encoded = btoa(binaryStr).replace(/=+$/, '');

    // Auth0 の ext-params は最大255文字。超過する場合は広告パラメータなしでログイン続行する。
    if (encoded.length > AUTH0_EXT_PARAMS_MAX_LENGTH) {
      return undefined;
    }

    return encoded;
  } catch {
    // エンコード失敗時は広告パラメータなしでログイン続行
    return undefined;
  }
};
